<script setup>
    const props = defineProps({
        quote: { type: Object, default: null },
        period: String,
    })

    const costs = computed(() => props.quote.costs[props.period.toLowerCase()])
    const takeHomePayBefore = computed(
        () => props.quote.takeHomePay.before[props.period.toLowerCase()],
    )
    const takeHomePayAfter = computed(
        () => props.quote.takeHomePay.after[props.period.toLowerCase()],
    )
    const savings = computed(() => props.quote.savings.total) // make this computed so it animates
</script>

<template>
    <div class="relative">
        <Transition>
            <div
                v-if="quote?.error"
                class="text absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/90 text-sm font-bold text-brand2"
            >
                <p>Error loading quote.</p>
                <p>
                    Please
                    <NuxtLink to="/contact-us" class="underline">
                        contact us
                    </NuxtLink>
                    to calculate your savings.
                </p>
            </div>
        </Transition>

        <dl>
            <div class="mt-3 text-xl leading-6 text-dark">
                <dt>Estimated novated lease payment</dt>
                <dd>
                    <Transition>
                        <span v-if="!quote?.costs">
                            <span class="placeholder"></span>
                        </span>
                        <span v-else :key="costs">
                            {{ prettyPrice(costs, true) }}*
                        </span>
                    </Transition>
                </dd>
            </div>

            <div class="mt-4 bg-light p-3 text-sm font-medium">
                <dt>
                    <Transition>
                        <span :key="period">
                            Your current estimated
                            <span>{{ period.toLowerCase() }}</span>
                            take home pay
                        </span>
                    </Transition>
                </dt>
                <dd>
                    <Transition>
                        <span v-if="!quote?.takeHomePay">
                            <span class="placeholder"></span>
                        </span>
                        <span v-else :key="takeHomePayBefore">
                            {{ prettyPrice(takeHomePayBefore, true) }}*
                        </span>
                    </Transition>
                </dd>
            </div>

            <div class="p-3 text-sm font-medium">
                <dt>
                    <Transition>
                        <span :key="period">
                            Your estimated
                            <span>{{ period.toLowerCase() }}</span>
                            take home pay after lease payments
                        </span>
                    </Transition>
                </dt>
                <dd>
                    <Transition>
                        <span v-if="!quote?.takeHomePay">
                            <span class="placeholder"></span>
                        </span>
                        <span v-else :key="takeHomePayAfter">
                            {{ prettyPrice(takeHomePayAfter, true) }}*
                        </span>
                    </Transition>
                </dd>
            </div>

            <div
                class="animated-gradient mb-5 bg-brand1 px-3 py-5 pt-4 text-sm font-medium text-white"
            >
                <dt>Estimated tax savings</dt>
                <dd>
                    <Transition>
                        <span v-if="!quote?.savings">
                            <span class="placeholder"></span>
                        </span>
                        <span v-else :key="savings">
                            {{ prettyPrice(savings, true) }}*
                        </span>
                    </Transition>
                </dd>
            </div>
        </dl>
    </div>
</template>

<style scoped>
    dl {
        dd {
            @apply relative;
        }

        > div {
            @apply flex;
            @apply gap-4;
            @apply justify-between;
        }
    }

    span {
        @apply transition;
    }

    .v-leave-active {
        position: absolute;
    }

    dd .v-leave-active {
        right: 0;
    }

    .placeholder {
        @apply block;
        @apply animate-pulse;
        @apply rounded-lg;
        @apply px-2;
        min-width: 70px;
        height: 100%;
        background-color: #aaa;

        &:before {
            content: '$';
        }
    }

    .text-white .placeholder {
        @apply bg-dark;
    }

    .animated-gradient {
        background-image: linear-gradient(
            100deg,
            #2b54ff,
            #12307e,
            #2b54ff,
            #12307e,
            #2b54ff
        );
        background-size: 400%;
        animation: animatedGradient 20s infinite;
    }

    @keyframes animatedGradient {
        0%,
        100% {
            background-position: 0% 30%;
        }
        50% {
            background-position: 100% 70%;
        }
    }
</style>
